'use client';

import React from 'react';
import classNames from 'classnames';
import { useTranslate } from '@tolgee/react';
import {
  Caption,
  EmployeeBaseCard,
  ChevronRightIcon,
  SubtleButtonLink,
  Subtitle,
  Text,
} from '@/design-system';
import {
  CardCategoryLabel,
  CardCategory,
} from '@/design-system/components/Basic/Card/CardCategoryLabel';
import type { PreventionAssignment } from '@/types/Api';
import { PreventionAssignmentStatusEnum, PreventionProfileCategoryEnum } from '@/types/Api';
import { formatDateToDe } from '@/utils/helpers';

/** Only statuses that show a tag (ALL_GOOD shows no tag) */
const STATUS_TAG_CLASS: Record<
  Exclude<PreventionAssignmentStatusEnum, PreventionAssignmentStatusEnum.ALL_GOOD>,
  string
> = {
  [PreventionAssignmentStatusEnum.DEADLINE_MISSED]: 'bg-ds-bg-feedback-soft-error text-ds-text-1',
  [PreventionAssignmentStatusEnum.DEADLINE_APPROACHING]:
    'bg-ds-bg-feedback-soft-info text-ds-text-1',
};

export interface EmployeePreventionCardProps {
  assignment: PreventionAssignment;
  /** Optional href. If not provided, the card is not clickable. */
  to?: string;
  className?: string;
  showDocumentAction?: boolean;
  /** Called when the right chevron is clicked. Stops propagation. */
  onAction?: () => void;
  /** Called when "Durchführung jetzt dokumentieren" is clicked. If provided, the button is an action (no href). Stops propagation. */
  onDocumentAction?: () => void;
}

/**
 * Prevention card for employee layout. Mandatory vs offer is derived from
 * assignment.preventionProfile?.category (MANDATORY → purple, due date, optional document button; OFFER → green, invitation date).
 */
export const EmployeePreventionCard: React.FC<EmployeePreventionCardProps> = ({
  assignment,
  to,
  className,
  showDocumentAction = false,
  onAction,
  onDocumentAction,
}) => {
  const { t } = useTranslate();
  const category =
    assignment.preventionProfile?.category ?? PreventionProfileCategoryEnum.MANDATORY;
  const isMandatory = category === PreventionProfileCategoryEnum.MANDATORY;

  const title =
    assignment.preventionTrigger?.name?.trim() ||
    assignment.preventionProfile?.description?.trim() ||
    '';
  const formattedDate = formatDateToDe(assignment.deadline);
  const status = assignment.status ?? PreventionAssignmentStatusEnum.ALL_GOOD;
  const statusKey = status.toLowerCase();
  const categoryKey = category.toLowerCase();
  const statusLabelKey = `ohs-hub.prevention.status.${categoryKey}.${statusKey}`;
  const statusLabel = status !== PreventionAssignmentStatusEnum.ALL_GOOD ? t(statusLabelKey) : null;

  const dateLabel = isMandatory
    ? t('ohs-hub.prevention.deadline')
    : t('ohs-hub.prevention.next_mailing_date');
  const footerLabel = isMandatory
    ? t('ohs-hub.prevention_types.mandatory')
    : t('ohs-hub.prevention_types.offer');

  const handleChevronClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAction?.();
  };

  const handleDocumentClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onDocumentAction?.();
  };

  const showTag = status !== PreventionAssignmentStatusEnum.ALL_GOOD && statusLabel;

  return (
    <EmployeeBaseCard to={to} className={className}>
      <div className="flex flex-col flex-1 gap-6">
        <div className="flex justify-between items-start gap-3 shrink-0 pl-4 pr-2">
          {title && (
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <Subtitle weight="semibold" className="line-clamp-3">
                {title}
              </Subtitle>
            </div>
          )}
          <div className="flex gap-2 justify-end items-center shrink-0">
            {showTag && (
              <div
                className={classNames(
                  'flex justify-center items-center gap-2.5 py-1 px-2 rounded-ds-2',
                  STATUS_TAG_CLASS[
                    status as Exclude<
                      PreventionAssignmentStatusEnum,
                      PreventionAssignmentStatusEnum.ALL_GOOD
                    >
                  ],
                )}
              >
                <Text size="s" weight="semibold" className="text-inherit">
                  {statusLabel}
                </Text>
              </div>
            )}
            {onAction ? (
              <button type="button" onClick={handleChevronClick} aria-label={title}>
                <ChevronRightIcon className="text-ds-navy-blue-900 w-4 h-4" aria-hidden />
              </button>
            ) : (
              <ChevronRightIcon className="text-ds-navy-blue-900 w-4 h-4" aria-hidden />
            )}
          </div>
        </div>

        <div className="flex flex-1 min-h-0 flex-col justify-center">
          <div className="flex justify-between items-center px-4">
            <div className="flex flex-col gap-2 min-w-0">
              <Caption weight="medium" className="text-ds-text-3">
                {dateLabel}
              </Caption>
              <Text size="s" weight="semibold" className="text-ds-text-1">
                {formattedDate}
              </Text>
            </div>

            {showDocumentAction && isMandatory && onDocumentAction && (
              <div className="flex justify-end">
                <SubtleButtonLink onClick={handleDocumentClick}>
                  {t('ohs-hub.prevention.list.document')}
                </SubtleButtonLink>
              </div>
            )}
          </div>
        </div>
      </div>
      <CardCategoryLabel category={isMandatory ? CardCategory.MANDATORY : CardCategory.OFFER}>
        {footerLabel}
      </CardCategoryLabel>
    </EmployeeBaseCard>
  );
};
